import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BenchmarkData, BenchmarkResult } from '../types';
import { loadBenchmarkFile } from '../utils/loadBenchmarkData';
import './AIEggIndex.css';

const BENCHMARK_KEYS = ['practical_knowledge', 'ifeval', 'gsm8k', 'creative_technical'] as const;
// Overall/winner ranks only on the two custom benchmarks every provider runs (IFEval/GSM8K
// are Groq/Cohere-only), and only for models that have both — so no crowning on one score.
const OVERALL_KEYS = ['practical_knowledge', 'creative_technical'] as const;

const AIEggIndex: React.FC = () => {
  const [data, setData] = useState<BenchmarkData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBenchmarkFile<BenchmarkData>('latest.json')
      .then(data => {
        setData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const calculateOverall = (model: BenchmarkResult): number => {
    const scores = OVERALL_KEYS
      .map(key => model.benchmarks[key]?.score)
      .filter((s): s is number => typeof s === 'number' && s > 0);
    return scores.length === OVERALL_KEYS.length
      ? scores.reduce((a, b) => a + b, 0) / scores.length
      : NaN;
  };

  const getWinner = (): { model: BenchmarkResult; score: number } | null => {
    const ranked = (data?.models ?? [])
      .map(model => ({ model, score: calculateOverall(model) }))
      .filter(x => !Number.isNaN(x.score));
    if (!ranked.length) return null;
    return ranked.reduce((best, x) => (x.score > best.score ? x : best));
  };

  const winner = getWinner();
  const formatScore = (score: number) => (score * 100).toFixed(0) + '%';

  return (
    <section id="ai-egg-index" className="ai-egg-index-section">
      <div className="container">
        <h2 className="section-title">AI Egg Index</h2>
        <p className="section-description">
          Benchmarking free tier LLMs on tasks regular people actually care about.
          Updated weekly.
        </p>

        {loading ? (
          <div className="egg-loading">Loading benchmark data...</div>
        ) : winner ? (
          <div className="egg-preview">
            <h3 className="preview-title">This Week's Winner</h3>
            <div className="winner-card">
              <div className="winner-model">
                <span className="winner-icon">🥇</span>
                <span className="winner-name">{winner.model.model.split('/').pop()}</span>
                <span className={`winner-provider provider-${winner.model.provider}`}>
                  {winner.model.provider}
                </span>
              </div>
              <div className="winner-score">
                <span className="score-label">Overall Score</span>
                <span className="score-value">{formatScore(winner.score)}</span>
              </div>
              <div className="winner-breakdown">
                {BENCHMARK_KEYS.map(key => {
                  const score = winner.model.benchmarks[key]?.score;
                  if (!score) return null;
                  const shortName = key.replace('_', ' ').replace('practical knowledge', 'practical').replace('creative technical', 'creative');
                  return (
                    <div key={key} className="breakdown-item">
                      <span className="breakdown-name">{shortName}</span>
                      <span className="breakdown-score">{formatScore(score)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="models-tested">{data?.models?.length} models tested • Runs weekly • Updated {data?.last_updated}</p>
          </div>
        ) : (
          <div className="egg-loading">No benchmark data available yet.</div>
        )}

        <div className="egg-cta">
          <a
            href="https://ceaustin117.github.io/ai-egg-index/"
            target="_blank"
            rel="noopener noreferrer"
            className="egg-link"
          >
            Live Index ↗
          </a>
          <Link to="/blog/ai-egg-index" className="egg-link">
            Read the Story
          </Link>
          <Link to="/benchmarks" className="egg-link">
            About the Index
          </Link>
          <a
            href="https://github.com/Ceaustin117/ai-egg-index"
            target="_blank"
            rel="noopener noreferrer"
            className="egg-link egg-link-github"
          >
            View on GitHub
          </a>
        </div>
      </div>
    </section>
  );
};

export default AIEggIndex;
